import React, { useState } from "react";
import AnnotationModal from "./AnnotationModal";

/**
 * TimeSignatureSelector component for selecting time signatures for specific measures
 * Redesigned to match the TimeSignatureChangeSection UI
 */
const TimeSignatureSelector = ({ 
  measureIndex, 
  currentSignature, 
  onSelect, 
  onCancel,
  position
}) => {
  const timeSignatureOptions = ["4/4", "3/4", "2/4", "6/8", "9/8", "12/8", "Custom"];
  const [selectedTimeSignature, setSelectedTimeSignature] = useState(currentSignature);
  const [isCustom, setIsCustom] = useState(false);
  const [customTimeSignature, setCustomTimeSignature] = useState({
    numerator: 4,
    denominator: 4
  });
  const [error, setError] = useState("");

  // Initialize custom time signature if current signature is custom
  useState(() => {
    if (!timeSignatureOptions.includes(currentSignature)) {
      // Parse the custom time signature (e.g., "5/8")
      const [numerator, denominator] = currentSignature.split('/').map(num => parseInt(num, 10));
      
      if (!isNaN(numerator) && !isNaN(denominator)) {
        setCustomTimeSignature({
          numerator,
          denominator
        });
        setIsCustom(true);
        setSelectedTimeSignature("Custom");
      }
    }
  }, [currentSignature]);

  // Handle time signature change
  const handleTimeSignatureChange = (e) => {
    const value = e.target.value;
    setSelectedTimeSignature(value);
    
    // Check if custom option is selected
    if (value === "Custom") {
      setIsCustom(true);
    } else {
      setIsCustom(false);
    }
  };

  // Handle custom numerator change
  const handleCustomNumeratorChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setCustomTimeSignature({ ...customTimeSignature, numerator: value });
    }
  };

  // Handle custom denominator change
  const handleCustomDenominatorChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && [2, 4, 8, 16].includes(value)) {
      setCustomTimeSignature({ ...customTimeSignature, denominator: value });
    }
  };

  // Handle save
  const handleSave = () => {
    let timeSignatureValue = selectedTimeSignature;
    
    // If custom is selected, use the custom time signature
    if (isCustom) {
      timeSignatureValue = `${customTimeSignature.numerator}/${customTimeSignature.denominator}`;
    }
    
    // Check if the new time signature is the same as the current one
    // If it is, there's no need to change anything
    if (timeSignatureValue === currentSignature) {
      // Close the selector without making any changes
      onCancel();
      return;
    }
    
    // Clear previous errors
    setError("");
    
    // Send the selected time signature back to the parent component
    onSelect(measureIndex, timeSignatureValue);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <AnnotationModal
        title={`Time Signature (拍号) - Measure ${measureIndex}`}
        onSave={handleSave}
        onCancel={onCancel}
        isEditing={true}
        error={error}
      >
        <div className="flex items-center space-x-2 mb-4">
          {isCustom ? (
            <>
              <div>
                <label className="block text-sm mb-1">Numerator</label>
                <input
                  type="number"
                  min="1"
                  value={customTimeSignature.numerator}
                  onChange={handleCustomNumeratorChange}
                  className="w-20 px-3 py-2 bg-gray-200 rounded-md"
                />
              </div>
              <div className="flex items-end pb-1 font-bold">/</div>
              <div>
                <label className="block text-sm mb-1">Denominator</label>
                <select
                  value={customTimeSignature.denominator}
                  onChange={handleCustomDenominatorChange}
                  className="w-20 px-3 py-2 bg-gray-200 rounded-md"
                >
                  {[2, 4, 8, 16].map(value => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div className="w-full">
              <label className="block text-sm mb-1">Time Signature (拍号)</label>
              <select
                value={selectedTimeSignature}
                onChange={handleTimeSignatureChange}
                className="w-full px-3 py-2 bg-gray-200 rounded-md"
              >
                {timeSignatureOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AnnotationModal>
    </div>
  );
};

export default TimeSignatureSelector;
